import type { FocusData } from '../../../../../api/initiative-client/initiative-client.type';
import type { Board, Team } from '../../../../../api/projects-client/projects-client.type';
import type { ChartDataItem } from './focus-chart.type';

/**
 * Generate a mapping of selection item IDs to their names
 *
 * @param selection Array of teams or boards to generate name mapping for
 * @returns Record mapping selection IDs to their display names
 */
const getSelectionNames = (selection: Team[] | Board[]): Record<string, string> => {
  const selectionNames: Record<string, string> = {};

  selection.forEach((item) => {
    selectionNames[item.id] = item.name;
  });

  return selectionNames;
};

/**
 * Check if focus data exists and contains any entries
 *
 * @param focusData Focus data object to check, or null if no data available
 * @returns True if focus data exists and has entries, false otherwise
 */
const hasFocusData = (focusData: FocusData | null): boolean => {
  if (!focusData) {
    return false;
  }

  return Object.keys(focusData).length > 0;
};

/**
 * Formats focus chart data for Flora AI analysis.
 *
 * @param {ChartDataItem[]} chartData - The chart data to format
 * @param {Team[] | Board[]} selection - The selected teams or boards
 * @returns {Record<string, Record<string, string>>} Formatted data with dates as keys and team/board names with their focus values
 */
const formatFocusDataForFlora = (
  chartData: ChartDataItem[],
  selection: Team[] | Board[],
): Record<string, Record<string, string>> => {
  const formattedData: Record<string, Record<string, string>> = {};
  const idToNameMap = getSelectionNames(selection);

  chartData.forEach((item) => {
    const dateData: Record<string, string> = {};

    selection.forEach((selectionItem) => {
      const itemId = selectionItem.id;
      const itemName = idToNameMap[itemId];
      const value = item[itemId];
      dateData[itemName] = typeof value === 'number' ? `${value}%` : '0%';
    });

    formattedData[item.date] = dateData;
  });

  return formattedData;
};

export { formatFocusDataForFlora, getSelectionNames, hasFocusData };
